import React, { useCallback, useMemo } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";
import { AHSeparator, ListItem } from "arkh";
import { TeamsAdminQuery } from "./__generated__/TeamsAdminQuery.graphql";
import { FlatList, View } from "react-native";
import { TeamIconCircle } from "../../components/mobile/TeamIconCircle";

import { StoreAdminNavigationScreenProps } from "./StoreAdminNavigation";
import { useIntlTokens } from "../../components/mobile/useIntlTokens";
import { useIntl } from "react-intl";
import { ActionFooter } from "@/src/components/ActionFooter";
import { SafeAreaProvider } from "react-native-safe-area-context";
export function TeamsAdmin(
  props: StoreAdminNavigationScreenProps<"TeamsAdmin">
) {
  return (
    <SafeAreaProvider>
      <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
        {(fetchKey) => <InnerTeamsAdmin {...props} fetchKey={fetchKey} />}
      </LazyQueryNetworkBoundrary>
    </SafeAreaProvider>
  );
}

function InnerTeamsAdmin(
  props: StoreAdminNavigationScreenProps<"TeamsAdmin"> & { fetchKey: string }
) {
  const data = useLazyLoadQuery<TeamsAdminQuery>(
    graphql`
      query TeamsAdminQuery {
        currentStore @required(action: THROW) {
          allTeams(includeArchived: true) {
            id
            isArchived
            name
            isPrivate
            ...TeamIconCircleComponent_Team
          }
        }
      }
    `,
    {},
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const hasArchivedTeams = useMemo(
    () => data.currentStore.allTeams.some((team) => team.isArchived),
    [data.currentStore.allTeams]
  );

  const teams = useMemo(
    () =>
      data.currentStore.allTeams
        .filter((team) => !team.isArchived)
        .sort((a, b) => {
          const ap = a.isPrivate ? 1 : 0;
          const bp = b.isPrivate ? 1 : 0;
          if (bp - ap === 0) {
            return a.name.localeCompare(b.name);
          } else {
            return bp - ap;
          }
        }),
    [data.currentStore.allTeams]
  );

  const renderItem = useCallback(
    ({ item, index }: { item: (typeof teams)[number]; index: number }) => {
      return (
        <ListItem
          onPress={() => {
            props.navigation.navigateDeprecated("TeamAdmin", {
              teamId: item.id,
            });
          }}
          left={<TeamIconCircle size="smallMedium" team={item} />}
          right={<ListItem.NavArrow />}
        >
          <ListItem.Title
            color={item.isArchived ? "text-secondary" : "text-primary"}
          >
            {item.name}
          </ListItem.Title>
        </ListItem>
      );
    },
    [props.navigation]
  );
  const intl_tokens = useIntlTokens();
  const intl = useIntl();

  return (
    <View style={{ flex: 1 }}>
      <FlatList
        ItemSeparatorComponent={AHSeparator}
        ListFooterComponent={AHSeparator}
        data={teams}
        renderItem={renderItem}
        keyExtractor={(item) => item.id}
      />
      <ActionFooter
        safeInsetBottom
        primary={
          <ActionFooter.Primary
            title={intl_tokens.CreateTeam}
            icon="team"
            onPress={() => {
              props.navigation.navigateDeprecated("TeamCreate", {});
            }}
          />
        }
        secondary={
          hasArchivedTeams ? (
            <ActionFooter.Secondary
              title={intl.formatMessage({
                defaultMessage: "Show Archived Teams",
                description: "Show archived teams button",
              })}
              icon="archive"
              onPress={() => {
                props.navigation.navigateDeprecated("ArchivedTeams", {});
              }}
            />
          ) : null
        }
      />
    </View>
  );
}
